
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { CreatorBasicInfo } from "./creator-form/CreatorBasicInfo";
import { CreatorContactInfo } from "./creator-form/CreatorContactInfo";
import { CreatorWebPresence } from "./creator-form/CreatorWebPresence";
import { ToggleLeft, ToggleRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { CreatorProviderInfo } from "./creator-form/CreatorProviderInfo";
import { CreatorCertificateInfo } from "./creator-form/CreatorCertificateInfo";

interface Creator {
  creator_id: number;
  name: string;
  surname: string;
  nick_name?: string;
  email: string;
  phone_number: string;
  shop?: string;
  web_page?: string;
  bio?: string;
  instagram_url?: string;
  facebook_url?: string;
  behance_url?: string;
  pinterest_url?: string;
  verified?: boolean;
  is_ambasador?: boolean;
  is_seller?: boolean;
  certificate_template?: number | null;
  default_provider?: number | null;
}

interface Template {
  template_id: number;
  name: string;
  type: string;
}

interface CreatorEditDialogProps {
  creator: Creator | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreatorEditDialog({
  creator,
  open,
  onOpenChange,
}: CreatorEditDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Creator | null>(creator);

  const { data: templates } = useQuery({
    queryKey: ["certificate-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("templates")
        .select("*")
        .eq("type", "certificate");

      if (error) throw error;
      return data as Template[];
    },
  });

  useEffect(() => {
    setFormData(creator);
  }, [creator]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!formData) return;
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleTemplateChange = (value: string) => {
    if (!formData) return;
    setFormData({
      ...formData,
      certificate_template: value === "none" ? null : parseInt(value),
    });
  };

  const handleProviderChange = (value: string) => {
    if (!formData) return;
    setFormData({
      ...formData,
      default_provider: value === "none" ? null : parseInt(value),
    });
  };

  const handleToggleStatus = async (field: 'verified' | 'is_ambasador' | 'is_seller') => {
    if (!formData) return;
    try {
      const newStatus = !formData[field];
      
      const { error } = await supabase
        .from("creators")
        .update({ [field]: newStatus })
        .eq("creator_id", formData.creator_id);

      if (error) throw error;

      setFormData({
        ...formData,
        [field]: newStatus,
      });

      toast({
        title: "Status updated",
        description: `Creator ${field.replace('_', ' ')} status has been updated successfully.`,
      });
    } catch (error) {
      console.error(`Error updating creator ${field} status:`, error);
      toast({
        title: "Error",
        description: `Failed to update creator ${field.replace('_', ' ')} status. Please try again.`,
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData) return;

    try {
      const { error } = await supabase
        .from("creators")
        .update({
          name: formData.name,
          surname: formData.surname,
          nick_name: formData.nick_name,
          email: formData.email,
          phone_number: formData.phone_number,
          shop: formData.shop,
          web_page: formData.web_page,
          bio: formData.bio,
          instagram_url: formData.instagram_url,
          facebook_url: formData.facebook_url,
          behance_url: formData.behance_url,
          pinterest_url: formData.pinterest_url,
          verified: formData.verified,
          is_ambasador: formData.is_ambasador,
          is_seller: formData.is_seller,
          certificate_template: formData.certificate_template,
          default_provider: formData.default_provider,
        })
        .eq("creator_id", formData.creator_id);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ["creators-list"] });
      
      toast({
        title: "Success",
        description: "Creator information updated successfully.",
      });
      
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating creator:", error);
      toast({
        title: "Error",
        description: "Failed to update creator information. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (!formData) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Creator Information</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center justify-between space-x-4">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => handleToggleStatus('verified')}
                className="flex items-center space-x-2 hover:text-primary"
              >
                {formData.verified ? (
                  <ToggleRight className="h-5 w-5" />
                ) : (
                  <ToggleLeft className="h-5 w-5" />
                )}
                <Badge variant={formData.verified ? "default" : "secondary"}>
                  {formData.verified ? "Verified" : "Unverified"}
                </Badge>
              </button>

              <button
                type="button"
                onClick={() => handleToggleStatus('is_ambasador')}
                className="flex items-center space-x-2 hover:text-primary"
              >
                {formData.is_ambasador ? (
                  <ToggleRight className="h-5 w-5" />
                ) : (
                  <ToggleLeft className="h-5 w-5" />
                )}
                <Badge variant={formData.is_ambasador ? "default" : "secondary"}>
                  {formData.is_ambasador ? "Ambassador" : "Not Ambassador"}
                </Badge>
              </button>

              <button
                type="button"
                onClick={() => handleToggleStatus('is_seller')}
                className="flex items-center space-x-2 hover:text-primary"
              >
                {formData.is_seller ? (
                  <ToggleRight className="h-5 w-5" />
                ) : (
                  <ToggleLeft className="h-5 w-5" />
                )}
                <Badge variant={formData.is_seller ? "default" : "secondary"}>
                  {formData.is_seller ? "Seller" : "Not Seller"}
                </Badge>
              </button>
            </div>

            <CreatorProviderInfo 
              formData={formData} 
              onProviderChange={handleProviderChange} 
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CreatorCertificateInfo 
              formData={formData} 
              onTemplateChange={handleTemplateChange}
              templates={templates}
            />
          </div>

          <CreatorBasicInfo formData={formData} handleInputChange={handleInputChange} />
          <CreatorContactInfo formData={formData} handleInputChange={handleInputChange} />
          <CreatorWebPresence formData={formData} handleInputChange={handleInputChange} />

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">Save Changes</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
